// What runs in a browser at a desk.
//
// THE STYLESHEET ORDER IS THE DESIGN RULING, EXECUTED. XP1's tokens load first,
// the console's density and desk identity second, the dark selectors last.
// Colour is declared by the console now: tokens/console.ts says why. Swapping
// the first two would restore XP1's hall-scale type and touch sizes, which
// would look like a styling bug and is the sibling relationship running
// backwards. check.mjs asserts the order in this file, because it is one of
// the two places the relationship is expressed — the other is which tokens
// src/tokens/console.css is permitted to declare at all.
//
// SIX IMPORTS, TWO VOCABULARIES, AND THE SPLIT IS NOT ARBITRARY. --d-* is the
// shared desk vocabulary, drawn by furniture both desks use; --xp-* is this
// console's own, which XP1's contrast function knows how to measure. They carry
// the same XP1 values in the same modes, and check.mjs asserts that pairing
// token by token rather than trusting it — the point of the shared package is
// that the two desks cannot drift, and two hand-maintained copies of one palette
// is exactly how they would.
//
// Furniture loads after both token layers because it consumes them. kit.css
// loads after furniture, because it restyles the shared parts, and both load
// before console.css so a screen in this package can overrule a shared
// component without editing the shared component.
//
// Thin on purpose, matching xp/kiosk/src/main.tsx. Everything worth asserting is
// in App and below it, where a check can drive it with an injected transport and
// an injected store; this file is the two lines that cannot be driven from
// anywhere except a real browser.

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "./theme/theme.js";
import "@nova/xp-ui/tokens.css";
import "@nova/desk-ui/tokens.css";
import "./tokens/console.css";
import "./tokens/desk.css";
import "@nova/desk-ui/furniture.css";
import "@nova/desk-ui/kit.css";
import "./console.css";
// Last, and per screen. Each of these wins a tie with console.css at equal
// specificity, which is what lets them be plain class names rather than a nest
// of overrides. The note at the top of kiosk.css carries the argument for why
// the longest screens keep their own sheet instead of adding to console.css.
import "./kiosk.css";
import "./registration.css";
import "./reports.css";
import "./clients.css";
import "./people.css";
import "./unverified.css";
import "./activity.css";
import "./themes.css";
import "./team.css";

import { installScrollableRegions } from "@nova/desk-ui/states";
import { App } from "./surfaces/App.js";

// A wide table's sideways scroll is reachable by keyboard (WCAG 2.1.1); see the helper.
installScrollableRegions();

const where = document.getElementById("console");
if (where === null) throw new Error("index.html has no #console to render into.");

createRoot(where).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
